import { useFormikContext } from 'formik';
import { addressService } from 'helpers/services';
import { isEmpty, get } from 'lodash-es';
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { InputActionMeta, ActionMeta, OptionsType, InputActionTypes } from 'react-select';

import * as errors from 'utils/errors';
import { debounceAsync } from 'utils/helpers';

import AutoComplete, { Props as AutoCompleteProps } from '../../AutoComplete';
import Text from '../../Text';
import * as helpers from '../helpers';
import * as Interfaces from '../interfaces';
import Details, { ComponentProps as DetailProps } from './Details';
import * as Styled from './styled';

export type DebounceOptions =
  | {
      enabled: true;
      timeout: number;
    }
  | { enabled: false };

export type ComponentProps = Pick<
  AutoCompleteProps.ComponentProps,
  | 'minLength'
  | 'cacheOptions'
  | 'defaultOptions'
  | 'loadOptions'
  | 'isClearable'
  | 'aria-label'
  | 'hint'
> & {
  /**
   * Label to show at top of the Address field
   */
  label: string;
  /**
   * (Optional) show addresses including PO boxes. Defaults to false
   */
  displayPOBox?: boolean;
  /**
   * (Optional) which address dataset should be used based on country. Defaults to AUS
   */
  country?: 'AUS' | 'NZL';
  /** Name of the field for AddressData information */
  fieldName: string;
  /**
   * function that resolves with the full data of the address
   * @param addressId unique id of the address to load
   */
  loadAddressFullDetailsHandler: (
    addressId: string,
    country: 'AUS' | 'NZL',
    selectedValue: AutoCompleteProps.OptionType
  ) => Promise<Interfaces.ApiFullAddress>;
  /**
   * Optional handler to display an option when no result was found (defualts to "Can't find a matching address? Enter manually")
   * @param inputValue textbox search value
   */
  notFoundAddress?: (inputValue: string) => string;

  /**
   * Optional props to be passed through to the full detail form. This is useful for adding/overriding details nested within the form (ie. labels)
   */
  fullFormProps?: Omit<DetailProps, 'fieldName'>;
  /**
   * Optional parameter for restricting manual address entry
   */
  allowManualAddressEntry?: boolean;

  /**
   * When true, will persist the field label when the user uses a manual entry form
   */
  persistLabelManualEntry?: boolean;

  /**
   * Whether to include the LGA on the fullDetails payload
   * @default false
   */
  includeLga?: boolean;

  /**
   * (Optional) Debounce configuration for loadOptions method. If unset, no debouncing will occur.
   */
  debounceOptions?: DebounceOptions;
};

export const Single: React.FC<ComponentProps> = ({
  fieldName,
  label,
  notFoundAddress = () => "Can't find a matching address? Enter manually.",
  displayPOBox = false,
  country = 'AUS',
  isClearable = true,
  loadOptions: loadAddressesHandler,
  loadAddressFullDetailsHandler,
  fullFormProps,
  allowManualAddressEntry = true,
  persistLabelManualEntry,
  includeLga,
  debounceOptions = { enabled: false },
  ...autoCompleteProps
}) => {
  // keep all addresses lookup results
  const loadingOptionsResult = useRef<OptionsType<AutoCompleteProps.OptionType>>([]);

  /**
   * Keep record of user action while using the <Single /> auto-search box
   */
  const previousAction = useRef<InputActionTypes>();
  /**
   * Keep record of the user keyboard strokes for avoiding pressing "tab" key will trigger the "onChange" event
   */
  const selectedKeyCode = useRef<number>(0);

  // access formik context data, and apply custom extraction of the actual form Data as Interfaces.AddressData (fieldName can be pointing to a root property of the form values or a sub key)
  const { values, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const addressValues = get(values, fieldName) as Interfaces.AddressData;
  if (!addressValues) {
    throw new errors.SVError(
      `invalid fieldName "${fieldName}" was provided to the <Single /> address component`
    );
  }

  // keep track of user search textbox input in auto mode
  const [inputValue, setInputValue] = useState(addressValues.summaryData.label);

  // toggle between auto / manual addresses
  const [manual, setManual] = useState(helpers.isManualAddress(addressValues));

  // Keep record of previous address value when user clicks input
  const previousAddress = useRef<AutoCompleteProps.OptionType>(addressValues.summaryData);

  /**
   * Clears the autocompleted fields for the address and any previous selected value
   */
  const resetValues = useCallback(() => {
    const initialValues = helpers.initialSingleAddressValues;
    setFieldValue(`${fieldName}.summaryData`, initialValues.summaryData, false);
    setFieldValue(`${fieldName}.fullDetails`, initialValues.fullDetails, true);
    previousAddress.current = initialValues.summaryData;
    setInputValue('');
  }, [fieldName, setFieldValue]);

  /**
   * Handler function for selecting the "goto Manual" mode
   * @param e Event object
   */
  const onSelectManual = useCallback(
    (event?: React.MouseEvent) => {
      // event can come from the text link or from the AutoComplete
      event && event.preventDefault();
      resetValues();
      setFieldTouched(fieldName, helpers.initialSingleAddressTouched(false) as any, false);
      setManual(true);
    },
    [fieldName, setFieldTouched, resetValues]
  );

  /**
   * Handler function for returing to "Auto complete" mode
   * @param e Event object
   */
  const onBackToAutoComplete = useCallback(
    (event: any) => {
      event.preventDefault();
      resetValues();
      setFieldTouched(fieldName, helpers.initialSingleAddressTouched(false) as any, false);
      setInputValue(addressValues.summaryData.label);
      setManual(false);
    },
    [fieldName, resetValues, setFieldTouched, addressValues.summaryData.label]
  );

  /**
   * <AutoComplete /> change event on selecting an item/option from the list
   * @param e selectable item
   */
  const onChange = useCallback(
    (
      selectedValue: AutoCompleteProps.Value,
      actionMeta: ActionMeta<AutoCompleteProps.OptionType>
    ) => {
      // User selected the empty option from the search, therefore needs to trigger a click on "enter manual" mode

      if (selectedKeyCode.current === 9) {
        // Tab key was pressed
        const value: AutoCompleteProps.OptionType = {
          label: inputValue,
          value: ''
        };
        setFieldValue(`${fieldName}.summaryData`, value, true);
      }
      // User has clicked the "clear" indicator icon
      else if (actionMeta.action === 'clear') {
        resetValues();
      } else {
        // Other user keystroke or mouse click
        if (loadingOptionsResult.current.length === 0) {
          if (allowManualAddressEntry) {
            onSelectManual();
          } else {
            resetValues();
          }
        } else {
          // user selected a single address
          const summaryData = selectedValue as AutoCompleteProps.OptionType;

          const runAsync = async (summaryData: AutoCompleteProps.OptionType) => {
            if (!summaryData) {
              resetValues();
              return;
            }
            // Retrieve the full address information from the api
            const fullAddressDetails = await loadAddressFullDetailsHandler(
              summaryData.value,
              country,
              summaryData
            );

            // Transform the api result to the form data object
            const fullDetails: Interfaces.FullDetails = {
              id: fullAddressDetails.id,
              fullAddress: `${summaryData.label}`,
              postCode: fullAddressDetails.postCode,
              state: fullAddressDetails.stateOrTerritory,
              suburb: fullAddressDetails.suburbOrPlaceOrLocality,
              ...(includeLga ? { lga: fullAddressDetails.lga } : {})
            };

            previousAddress.current = summaryData;

            // Persist the result to the form internal context and update the AutoComplete selected option "input" prop also (the AutoComplete "value" prop is auto updated internally via its "name" prop below)
            setFieldTouched(fieldName, helpers.initialSingleAddressTouched(true) as any, false);
            setFieldValue(`${fieldName}.summaryData`, summaryData, true);
            setFieldValue(`${fieldName}.fullDetails`, fullDetails, false);
          };
          // Rertieve the full details object from the summary object from API 'addresses' async handler
          runAsync(summaryData);
        }
      }
    },
    [
      resetValues,
      fieldName,
      setFieldTouched,
      setFieldValue,
      inputValue,
      loadAddressFullDetailsHandler,
      onSelectManual,
      country,
      allowManualAddressEntry,
      includeLga
    ]
  );

  /**
   * Utility function to execute the fetching of remote addresses for user typings
   * @param search search input
   */
  const loadOptions = useCallback(
    async (search: string) => {
      const queryString = `${addressService.searchUri}${
        !displayPOBox ? '?type=physical&' : '?'
      }country=${country}&search=${encodeURIComponent(search)}`;
      const results = await loadAddressesHandler(queryString);
      loadingOptionsResult.current = results;

      if (isEmpty(loadingOptionsResult.current)) {
        return [{ label: notFoundAddress(search), value: 'not-found' }];
      } else {
        return loadingOptionsResult.current;
      }
    },
    [country, displayPOBox, loadAddressesHandler, notFoundAddress]
  );

  /**
   * Utility function to debounce the fetching of remote addresses for user typings
   * @param search search input
   */
  const debounceLoadOptions = useMemo(
    () =>
      debounceOptions.enabled ? debounceAsync(loadOptions, debounceOptions.timeout) : loadOptions,
    [loadOptions, debounceOptions]
  );

  /**
   * <AutoComplete /> onInputChange event (Handle change events on the input)
   * @param newValue new user input
   * @param action user action meta
   */
  const onInputChange = useCallback(
    (newValue = '', { action }: InputActionMeta) => {
      // https://github.com/JedWatson/react-select/issues/3605#issuecomment-498650989
      // https://stackoverflow.com/questions/56442162/how-to-persist-intermittent-text-on-react-select
      // https://stackoverflow.com/questions/56900405/how-to-keep-options-state-after-losing-focus-on-async-select

      if (
        action === 'menu-close' &&
        previousAction.current === 'input-blur' &&
        !addressValues.summaryData.value
      ) {
        // User typed inside the auto-complete but didn't select anything from the drop-down menu => trigger an event with an empty selected value (using the "onBlur" event won't work because it fails when there was a pre-selected value from the drop-down and user types again with a new search,
        // causing the previous selected value from "addressValues" to take priority in the event). The extra check of `addressValues.summaryData.value` is needed to prevent case that after the user picked an item from the drop-down, the "onInputValue" method gets called twice with "input-blur" and "menu-close" actions
        setFieldValue(`${fieldName}.summaryData`, previousAddress.current, true);
      } else if (action === 'menu-close' && previousAction.current === 'set-value') {
        // User typed inside the auto-complete and selected an item from the drop-down menu ==> The "onChange" event will gets triggered
      }

      // Updagte the previous selected action with the current user action
      previousAction.current = action;

      if (action === 'input-change') {
        const value: AutoCompleteProps.OptionType = {
          label: '',
          value: ''
        };
        previousAddress.current = value;
      }

      // Keep the text the user typed inside the search box if he closed the menu or typed a character inside
      if (action !== 'input-blur' && action !== 'menu-close') {
        setInputValue(newValue);
        return newValue;
      } else if (previousAddress.current.value && !addressValues.summaryData.value) {
        // Triggered when the user has closed the menu without selecting a value from the list, has previously selected an address and has not changed the input. This ensures the address is kept if the user clicks on the input and then clicks away without changing any values
        setFieldValue(`${fieldName}.summaryData`, previousAddress.current, true);
      }
    },
    [fieldName, setFieldValue, addressValues.summaryData.value]
  );

  /**
   * <AutoComplete /> "keyDown" event when user presses the "tab" key code
   */
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    selectedKeyCode.current = e.keyCode;
  }, []);

  /**
   * <AutoComplete /> focus event on selecting an item/option from the list
   */
  const onFocus = useCallback(() => {
    const value: AutoCompleteProps.OptionType = {
      label: '',
      value: ''
    };
    setFieldValue(`${fieldName}.summaryData`, value, false);
  }, [fieldName, setFieldValue]);

  /**
   * <AutoComplete /> clear event on clearing the selected input
   */
  const onClear = useCallback(
    (event?: React.SyntheticEvent) => {
      event && event.preventDefault();
      resetValues();
    },
    [resetValues]
  );

  return (
    <Styled.Wrapper>
      {manual ? (
        <Styled.DetailsWrapper>
          <Styled.BackToManualAddressLinkWrapper>
            {persistLabelManualEntry && <Styled.Label>{label}</Styled.Label>}
            <Text.Link
              target='_self'
              value={'Back to address search'}
              showBackChevron
              onClick={onBackToAutoComplete}
              data-testid={`${fieldName}-BackToAddressSearchLink`}
            />
          </Styled.BackToManualAddressLinkWrapper>
          <Details fieldName={fieldName} {...fullFormProps} />
        </Styled.DetailsWrapper>
      ) : (
        <AutoComplete
          name={`${fieldName}.summaryData`}
          label={label}
          loadOptions={debounceLoadOptions}
          inputValue={inputValue}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onClear={onClear}
          onInputChange={onInputChange}
          onChange={onChange}
          isClearable={isClearable}
          // noOptionsMessage
          {...autoCompleteProps}
        />
      )}
      {allowManualAddressEntry && !manual && (
        <Styled.ManualAddressLinkWrapper>
          <Text.Link
            target='_self'
            value='Enter address manually'
            onClick={onSelectManual}
            data-testid={`${fieldName}-ManualAddressLink`}
          />
        </Styled.ManualAddressLinkWrapper>
      )}
    </Styled.Wrapper>
  );
};

export default Single;
